import { cn } from '@/lib/utils'

// Stocky shows TWO independent statuses per stocktake and staff read them as a
// pair: how far the count got, and whether the correction ever reached Shopify.
// Same rounded pill and --status-* pairs as StatusBadge (POs), grouped by whose
// move it is (docs/designs/colour-pass.md): blue still in progress, amber
// waiting on someone, green done, grey closed or not started. Never the brand.
const BASE =
  'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none whitespace-nowrap'

const DRAFT = 'bg-[var(--status-draft-bg)] text-[var(--status-draft-fg)]'
const AWAITING = 'bg-[var(--status-awaiting-bg)] text-[var(--status-awaiting-fg)]'
const APPROVED = 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
const ARCHIVED = 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'

/** Count Status: "Count in progress" (blue) while counting. "Count completed"
 *  is amber while it waits in review for someone to apply it, and green once
 *  applied. A cancelled session never completed, so it gets the archived grey. */
export function CountStatusBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const [label, style] =
    status === 'counting'
      ? ['Count in progress', DRAFT]
      : status === 'cancelled'
        ? ['Count cancelled', ARCHIVED]
        : status === 'applied'
          ? ['Count completed', APPROVED]
          : ['Count completed', AWAITING]
  return (
    <span className={cn(BASE, style, className)} data-status={status}>
      {label}
    </span>
  )
}

/** Adjustment Status: "Not created" (grey) while the count is still open, "Not
 *  performed" once it's completed without being pushed (amber, someone needs to
 *  apply it) or cancelled (grey, nothing will happen), and "Performed" (green)
 *  after Shopify took it. */
export function AdjustmentStatusBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const [label, style] =
    status === 'applied'
      ? ['Performed', APPROVED]
      : status === 'counting'
        ? ['Not created', ARCHIVED]
        : status === 'cancelled'
          ? ['Not performed', ARCHIVED]
          : ['Not performed', AWAITING]
  return (
    <span className={cn(BASE, style, className)} data-adjustment-status={status}>
      {label}
    </span>
  )
}
